import { Fragment, type ReactNode } from 'react';
import { cn } from '../utils/cn';
import {
  erpPageBreadcrumbClass,
  erpPageBreadcrumbCurrentClass,
  erpPageBreadcrumbItemClass,
  erpPageBreadcrumbSepClass,
  erpPageHeaderActionsClass,
  erpPageHeaderClass,
  erpPageHeaderRowClass,
  erpPageHeaderTextClass,
  erpPageSubtitleAccentClass,
  erpPageSubtitleBodyClass,
  erpPageSubtitleClass,
  erpPageTitleClass,
} from '../styles/erp-page-header.shared';

export interface PageHeaderBreadcrumbItem {
  label: string;
  /** When true, rendered as the current/active crumb */
  current?: boolean;
}

export interface PageHeaderProps {
  title: ReactNode;
  subtitle?: string;
  /**
   * `label` = uppercase eyebrow (default, enterprise list pages).
   * `description` = sentence-case supporting line.
   */
  subtitleVariant?: 'label' | 'description';
  /** Show a small brand accent bar before the subtitle (enterprise list pages) */
  subtitleAccent?: boolean;
  /** Optional content between title and subtitle (e.g. status chips) */
  meta?: ReactNode;
  breadcrumb?: PageHeaderBreadcrumbItem[];
  actions?: ReactNode;
  /** Optional page-level tabs row rendered under the header */
  tabs?: ReactNode;
  className?: string;
}

/**
 * Reusable ERP page heading block: breadcrumb → title → subtitle (+ optional actions).
 * Typography/contrast come from shared `.erp-page-*` CSS (theme-compat).
 */
export function PageHeader({
  title,
  subtitle,
  subtitleVariant = 'label',
  subtitleAccent = true,
  meta,
  breadcrumb,
  actions,
  tabs,
  className,
}: PageHeaderProps) {
  const subtitleClass =
    subtitleVariant === 'description' ? erpPageSubtitleBodyClass : erpPageSubtitleClass;

  return (
    // Use a div — never <header> — so app chrome rules like
    // `.app-scaling header { height: 64px }` cannot crush page titles.
    <div className={cn(erpPageHeaderClass, className)}>
      {breadcrumb && breadcrumb.length > 0 && (
        <nav className={erpPageBreadcrumbClass} aria-label="Breadcrumb">
          {breadcrumb.map((item, index) => (
            <Fragment key={`${item.label}-${index}`}>
              {index > 0 && (
                <span className={erpPageBreadcrumbSepClass} aria-hidden>
                  /
                </span>
              )}
              <span
                className={cn(
                  item.current ? erpPageBreadcrumbCurrentClass : erpPageBreadcrumbItemClass
                )}
              >
                {item.label}
              </span>
            </Fragment>
          ))}
        </nav>
      )}

      <div className={erpPageHeaderRowClass}>
        <div className={erpPageHeaderTextClass}>
          <h1 className={erpPageTitleClass}>{title}</h1>
          {meta ? <div className="erp-page-header-meta">{meta}</div> : null}
          {subtitle ? (
            <div className={erpPageSubtitleAccentClass}>
              {subtitleAccent && subtitleVariant === 'label' && (
                <span className="erp-page-subtitle-bar" aria-hidden />
              )}
              <p className={subtitleClass}>{subtitle}</p>
            </div>
          ) : null}
        </div>
        {actions ? <div className={erpPageHeaderActionsClass}>{actions}</div> : null}
      </div>
      {tabs}
    </div>
  );
}
